import { Grid, Stack } from "@mui/material";
import { ProjectProgress } from "@stories/organisms/ProjectProgress/ProjectProgress";
import { useProjectContext } from "@hooks/context/useProjectContext";

import { useProfileContext } from "@hooks/context/useProfileContext";
import { SubTitleWithImage } from "@stories/atoms/SubTitle/SubTitle";
import * as RouteHelper from "@utils/routes";
import { PageBreadcrumbs } from "@stories/atoms/PageBreadcrumbs/PageBreadcrumbs";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import { useEffect, useRef, useState } from "react";
import { useAnalysis } from "@hooks/crud/useAnalysis";
import { MonthSelect } from "@stories/atoms/MonthSelect/MonthSelect";
import { generatePDFReport } from "@utils/pdf";
import { StyledButton } from "@stories/atoms/StyledButton/StyledButton";
import { useParams } from "react-router-dom";
import { useContractorPackage } from "@hooks/crud/useContractorPackages";
import {
  ProjectAnalysisAggregateLineItem,
  ReportAnalysisLineItem,
  getPackageAnalysisAcrossMonth,
  getReportForMonth,
} from "@interfaces/analysis";
import { PackageDetails } from "@stories/organisms/PackageDetails/PackageDetails";
import { ContractorPackageLineItem } from "@interfaces/contractorPackage";
import { PackageTools } from "@stories/organisms/PackageTools/PackageTools";
import {
  SocialCommitmentTypeHour,
  SocialCommitmentTypeSpend,
} from "@interfaces/commitment";
import { ProjectLoader } from "@stories/atoms/Loaders/ProjectLoader/ProjectLoader";
import { StyledCard } from "@stories/atoms/StyledCard/StyledCard";

export const PackageDashboard = () => {
  const [selectedSocialProcurementType, setSelectedSocialProcurementType] =
    useState<SocialCommitmentTypeHour | SocialCommitmentTypeSpend>(
      SocialCommitmentTypeHour.LocalJobsFirst
    );
  const { packageId } = useParams();

  const [mainAnalysis, setMainAnalysis] =
    useState<ProjectAnalysisAggregateLineItem>();

  const [reportData, setReportData] = useState<ReportAnalysisLineItem[]>();
  const [mainContractorPackage, setMainContractorPackage] =
    useState<ContractorPackageLineItem>();

  const { userProfile, userOrganisation } = useProfileContext();
  const { selectedProject, selectedProjectOrganisation } = useProjectContext();
  const today = new Date();
  const [reportDate, setReportDate] = useState(
    today < selectedProject.endDate ? today : selectedProject.endDate
  );
  const { getContractorPackage, isContractorPackageLoading } =
    useContractorPackage(selectedProject?.id, packageId);

  const { analysis, getAnalysis, isAnalysisLoading } = useAnalysis(packageId);

  useEffect(() => {
    getAnalysis(selectedProject.id);
    if (packageId) {
      getContractorPackage(packageId).then((s) => {
        if (s) {
          setMainContractorPackage(s);
        }
      });
    }
  }, [packageId]);

  useEffect(() => {
    if (analysis && packageId) {
      const analysisForMonth = getPackageAnalysisAcrossMonth(
        analysis,
        packageId,
        reportDate.getMonth(),
        reportDate.getFullYear()
      );
      if (analysisForMonth) {
        setMainAnalysis(analysisForMonth);
      }
      const reportForMonth = getReportForMonth(
        analysis,
        packageId,
        reportDate.getMonth(),
        reportDate.getFullYear()
      );
      setReportData(reportForMonth);
    }
  }, [packageId, analysis, reportDate]);

  const pieChart0 = useRef<HTMLDivElement>();
  const pieChart1 = useRef<HTMLDivElement>();
  const pieChart2 = useRef<HTMLDivElement>();
  const pieChart3 = useRef<HTMLDivElement>();
  const pieChart4 = useRef<HTMLDivElement>();
  const pieChart5 = useRef<HTMLDivElement>();
  const pieChart6 = useRef<HTMLDivElement>();
  const pieChart7 = useRef<HTMLDivElement>();

  // useEffect(() => {
  //   const loadData = async (packageId: string) => {
  //     const analysis = await getAnalysis(selectedProject.id, packageId);
  //     const contractorPackage = await getContractorPackage(packageId);
  //   };
  //   if (packageId) {
  //     loadData(packageId);
  //   }
  // }, []);

  if (
    !userOrganisation ||
    !analysis ||
    !mainAnalysis ||
    isAnalysisLoading ||
    !userProfile ||
    !selectedProjectOrganisation ||
    !selectedProject ||
    isContractorPackageLoading
  ) {
    console.log("DAshboard PAGE LOADER");
    return <ProjectLoader />;
  }
  const breadcrumbs = [
    {
      path: RouteHelper.packages(selectedProject.id),
      name: `${selectedProject.projectName} Packages`,
    },
    {
      path: RouteHelper.contractorDashboard(selectedProject.id),
      name: `${mainContractorPackage?.contractorName} Package`,
    },
  ];

  const handleDownload = () => {
    generatePDFReport(
      selectedProject,
      pieChart0,
      pieChart1,
      pieChart2,
      pieChart3,
      pieChart4,
      pieChart5,
      pieChart6,
      pieChart7
    );
  };
  return (
    <>
      <PageBreadcrumbs
        organisationLogo={
          mainContractorPackage
            ? mainContractorPackage.contractorLogo
            : selectedProjectOrganisation.organisationLogo
        }
        title={`${
          mainContractorPackage
            ? mainContractorPackage.contractorName
            : selectedProjectOrganisation.organisationName
        } Package`}
        links={breadcrumbs}
      />
      <StyledCard>
        <Grid container spacing={2} sx={{ padding: "12px" }}>
          <Grid item xs={12} md={8}></Grid>

          <Grid
            item
            xs={12}
            md={2}
            sx={{
              display: "flex",
              alignContent: "right",
              justifyContent: "right",
              margin: "auto",
            }}
            justifyContent={"right"}
          >
            <MonthSelect
              setDate={setReportDate}
              date={reportDate}
              minDate={selectedProject.startDate}
              maxDate={selectedProject.endDate}
            />
          </Grid>

          <Grid
            item
            xs={12}
            md={2}
            sx={{
              display: "flex",
              alignContent: "right",
              justifyContent: "right",
              margin: "auto",
            }}
            justifyContent={"right"}
          >
            <StyledButton
              variant="contained"
              startIcon={<CloudDownloadIcon />}
              onClick={handleDownload}
            >
              Download Report
            </StyledButton>
          </Grid>
        </Grid>
      </StyledCard>
      <StyledCard>
        <Grid container spacing={2} sx={{ padding: "12px" }}>
          <Grid
            item
            xs={12}
            md={12}
            sx={{
              alignContent: "Center",
              justifyContent: "center",
              margin: "auto",
              padding: "12px",
            }}
            justifyContent={"center"}
          >
            <Stack spacing={4}>
              {mainContractorPackage ? (
                <PackageDetails contractorPackage={mainContractorPackage} />
              ) : null}
              {/* <ProjectProgress
                setSelectedSocialProcurementType={(
                  socialFactor:
                    | SocialCommitmentTypeHour
                    | SocialCommitmentTypeSpend
                ) => {
                  setSelectedSocialProcurementType(socialFactor);
                }}
                analysis={mainAnalysis}
                charts={pieChart1}
                ref1={pieChart1}
                ref2={pieChart2}
                ref3={pieChart3}
                ref4={pieChart4}
                ref5={pieChart5}
                ref6={pieChart6}
                ref7={pieChart7}
              /> */}
            </Stack>
          </Grid>
        </Grid>
      </StyledCard>

      {reportData ? (
        <PackageTools
          socialProcurementType={selectedSocialProcurementType}
          reportDate={reportDate}
          loading={isAnalysisLoading || isContractorPackageLoading}
          reportData={reportData}
        />
      ) : null}
    </>
  );
};
